import { useSignalR } from 'BrokerTsLib/src/context/SignalRProvider'
import type { ReactNode } from 'react'
import { createContext, useContext, useEffect, useState } from 'react'

import type { PoleasImportPreviewRow, RowImportStatus } from '@/dtos/PoleasImport'
import { usePoleasImport, usePoleasImportPreview } from '@/hooks/usePoleas'

export const PoleasImportState = {
  new: 0,
  preview: 1
} as const

export type PoleasImportState = typeof PoleasImportState[keyof typeof PoleasImportState]

export type IPoleasImportContext = {
  importState: PoleasImportState
  importId: number
  totalOfferCount: number
  importedOfferCount: number
  previewRows: PoleasImportPreviewRow[]
  importInProgress: boolean
  previewCallInProgress: boolean
  showCancelButton: boolean
  showImportButton: boolean
  forceReloadImportForm: boolean
  finalImportInProgress: boolean
  getPreviewOffers: (importId: number, offerCount: number) => Promise<any>
  setImportInProgress: (isRunning: boolean) => void
  cancelImportInProgress: () => void
  cancelImport: () => void
  finishImport: () => Promise<boolean>
}

export const defaults: IPoleasImportContext = {
  importState: PoleasImportState.new,
  importId: 0,
  totalOfferCount: 0,
  importedOfferCount: 0,
  previewRows: [],
  importInProgress: false,
  previewCallInProgress: false,
  showCancelButton: false,
  showImportButton: false,
  forceReloadImportForm: false,
  finalImportInProgress: false,
  getPreviewOffers: () => { return null as any },
  setImportInProgress: () => { },
  cancelImportInProgress: () => { },
  cancelImport: () => { },
  finishImport: () => { return null as any }
}

const poleasImportContext = createContext<IPoleasImportContext>(defaults as any)

export type PoleasImportProviderProps = {
  children: ReactNode
}

export function PoleasImportProvider({ children }: PoleasImportProviderProps) {
  const previewRowEventName = 'PoleasImportPreviewRow'
  const importRowEventName = 'PoleasImportRow'

  const context = poleasImportContext

  const signalR = useSignalR()
  const hubId = signalR?.connectionId ?? ''

  const [importId, setImportId] = useState(0)
  const [importState, setImportState] = useState<PoleasImportState>(PoleasImportState.new)
  const [totalOfferCount, setTotalOfferCount] = useState(0)
  const [importedCount, setImportedCount] = useState(0)
  const [importInProgress, setImportInProgress] = useState(false) // celý import
  const [previewCallInProgress, setPreviewCallInProgress] = useState(false) // volání preview
  const [showCancelButton, setShowCancelButton] = useState(false) // zrušení importu po preview
  const [showImportButton, setShowImportButton] = useState(false) // dokončení importu po preview
  const [forceReloadImportForm, setForceReloadImportForm] = useState(false) // reload formuláře po zrušení

  const [previewRows, setPreviewRows] = useState<PoleasImportPreviewRow[]>([])

  const { mutateAsync: callPreview, reset: stopPreview } = usePoleasImportPreview()

  const { mutateAsync: callImport, isLoading: isImportingData } = usePoleasImport()

  const previewOffers = (newImportId: number, offerCount: number) => {
    setImportId(newImportId)
    setImportState(PoleasImportState.preview)
    setForceReloadImportForm(false)
    setImportInProgress(true)
    setPreviewCallInProgress(true)
    setTotalOfferCount(offerCount)
    setPreviewRows([])
    // callPreview se ukončí po preview všech offer. Pokud se přeruší, v catch by měl být CancelledError s code = "ERR_CANCELED"
    return callPreview({ importId: newImportId, hubId }).
      then(() => {
        setPreviewCallInProgress(false)
        setShowCancelButton(true)
        setShowImportButton(true)
      }).
      catch((x) => {
        setPreviewCallInProgress(false)
        setImportInProgress(false)
        setShowCancelButton(true)
      })
  }

  const cancelImport = () => {
    setImportState(PoleasImportState.new)
    setPreviewRows([])
    setImportInProgress(false)
    setImportedCount(0)
    setForceReloadImportForm(true)
  }

  const finishImport = () => {
    return callImport({ importId, hubId }).
      then(() => {
        return Promise.resolve(true)
      }).
      catch((x) => {
        //TODO ...
        setImportInProgress(false)
        return Promise.resolve(false)
      })
  }

  useEffect(() => {
    signalR?.on(previewRowEventName, (data: PoleasImportPreviewRow) => {
      setPreviewRows((prevRows => [...prevRows, data]
        .sort((a, b) => {
          // Sort by error first (items with errors come first)
          if (a.error && !b.error) return -1
          if (!a.error && b.error) return 1
          
          // If both have same error status, sort by leasingContractNumber
          return (a.leasingContractNumber || '').localeCompare(b.leasingContractNumber || '')
        })
      ))
    })
    signalR?.on(importRowEventName, (data: RowImportStatus) => {
      console.log('importRowEventName', data)

      if (data.ok) {
        setImportedCount((cnt => cnt + 1))
      }
      else {
        //TODO...
        console.log('Chyba poleas importu: ', data.errorMessage)
      }
    })

    return () => {
      signalR?.off(previewRowEventName)
      signalR?.off(importRowEventName)
    }
  }, [signalR])


  return (
    <context.Provider value={{
      importState: importState,
      importId: importId,
      totalOfferCount: totalOfferCount,
      importedOfferCount: importedCount,
      previewRows: previewRows,
      importInProgress: importInProgress,
      previewCallInProgress: previewCallInProgress,
      showCancelButton: showCancelButton,
      showImportButton: showImportButton,
      forceReloadImportForm: forceReloadImportForm,
      finalImportInProgress: isImportingData,
      setImportInProgress: setImportInProgress,
      getPreviewOffers: previewOffers,
      cancelImportInProgress: stopPreview,
      cancelImport: cancelImport,
      finishImport: finishImport
    }}>
      {children}
    </context.Provider>
  )
}

export const usePoleasImportContext = () => {
  const context = useContext(poleasImportContext)

  if (context === undefined || Object.keys(context).length == 0) {
    throw new Error('usePoleasImportContext must be used within a PoleasImportProvider!')
  }

  return context
}